import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Sparkles, ArrowRight, Activity } from 'lucide-react';

import HaTrendChart from './HaTrendChart';
import HaCoverageRadar from './HaCoverageRadar';
import { useHrAnalyticsApi } from '../../hr-analytics/config';

// ═══════════ NE İŞE YARAR ═══════════
// HA modülü olan müşteri için ana paneldeki "İK Zekâsı" bölümü. Eskiden burada YALNIZ dört
// düz sayı vardı (SmartStatsRow) — modülün ayırt edici tarafını hiç göstermiyordu; müşteri
// pahalı bir analitik modülü aldığında panelde gördüğü şey diğer kartlardan farksız birkaç
// rakamdı.
//
// Bu panel modülün GERÇEKTEN yaptığı işi öne çıkarır:
//   • Dönem anlatısı   — dil modelinin ürettiği, sayıları kaynak metriklerle DOĞRULANMIŞ özet.
//   • Devir EĞİLİMİ    — aylık seriden eğri alan grafiği (HaTrendChart).
//   • Anomali sayısı   — mevsimsellikten sapan dönemler (yapay zekâ İÇERMEZ, istatistik).
// (Metrik kartları sırayla kaldırıldı — gerekçeler aşağıda, kart kodlarının olduğu blokta.)
//
// ═══════════ DÜRÜSTLÜK SINIRI ═══════════
// Panelde hiçbir şey "yapay zekâ" diye ETİKETLENMEZ. Anlatıyı bir dil modeli üretir ama
// eğilim, anomali ve metrikler deterministik hesaplardır; hepsini aynı rozetin altına
// toplamak müşteriye yanlış bir şey satmak olurdu. Panelin görsel dili premium, iddiası dar.
// (Anlatının üstünde bir "Yapay zekâ özeti" rozeti denendi ve KALDIRILDI — ürün kararı.)
//
// ═══════════ GÖRSEL DİL ═══════════
// Kenarlık, Analiz Paneli kartlarının (HaShimmerCard) "kayan ışık" desenidir — panel ile
// modülün kendi paneli aynı dili konuşsun diye 2026-08-04 kullanıcı isteğiyle buraya da
// alındı (eski hover'da dönen @property `<angle>` varyantının yerine). Teknik aynı:
// padding-box kart yüzeyi + border-box gradient; gradient içindeki parlak segment
// background-position animasyonuyla kenarlık boyunca akar. `prefers-reduced-motion`
// sözleşmesi aynen korunur: animasyon durur, statik gradient kalır.

// Kart 6 sn'de akar; panel tam genişlikte olduğundan yüzde-tabanlı kayış aynı sürede çok
// daha hızlı görünürdü — algılanan hızı kartla eşlemek için tur süresi uzatıldı.
const FLOW_DURATION_S = 9;

const flow = keyframes`
  0%   { background-position: 0% 50%, 0 0, 0% 50%; }
  100% { background-position: 0% 50%, 0 0, 300% 50%; }
`;

const Panel = styled.section`
  position: relative;
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  padding: 20px 22px;
  /* Yüzey: kart beyazının üstünde çok hafif bir marka parıltısı — okunabilirliği bozmayacak
     kadar zayıf (%4/%6), çünkü panelin içinde uzun bir metin var. */
  background:
    linear-gradient(
      135deg,
      rgba(2, 145, 239, 0.045) 0%,
      rgba(2, 211, 191, 0.06) 55%,
      rgba(255, 255, 255, 0) 100%
    ) padding-box,
    linear-gradient(${({ theme }) => theme.colors.surface.card}, ${({ theme }) => theme.colors.surface.card}) padding-box,
    linear-gradient(
      100deg,
      ${({ theme }) => theme.colors.primary.main} 0%,
      ${({ theme }) => theme.colors.secondary.main} 35%,
      ${({ theme }) => theme.colors.primary.light} 50%,
      ${({ theme }) => theme.colors.secondary.main} 65%,
      ${({ theme }) => theme.colors.primary.main} 100%
    ) border-box;
  background-size: auto, auto, 300% 100%;
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  animation: ${flow} ${FLOW_DURATION_S}s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PanelHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GradientDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary.main},
    ${({ theme }) => theme.colors.secondary.main}
  );
  color: #fff;
`;

const MoreLink = styled.button`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};

  &:hover {
    text-decoration: underline;
  }
`;

// Anlatı ÜSTTE tam genişlik (uzun bir metin, dar sütunda okunmuyordu), grafikler ALTINDA
// yan yana. Kullanıcı isteği ve okuma sırası da bu: önce "ne oldu", sonra "hangi şekilde".
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 0.85fr) minmax(260px, 1.15fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Narrative = styled.div`
  min-width: 0;
`;


const NarrativeText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.muted};
`;


const AnomalyLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

// ═══════════ HANGİ METRİK NEREDE ═══════════
// Eğilim grafiğinin metriği: devir oranı. İK'nın amiral KPI'ı ve yönü doğrudan aksiyona
// dönüşür. Serisi yoksa grafik HİÇ çizilmez — düz bir çizgi çizip yön ima etmek yanıltırdı.
const TREND_CODE = 'turnover.total.rate';

// Panelde metrik KARTI kalmadı — hepsi bilinçli kararlarla teker teker kaldırıldı:
//   • `headcount.active` — o sayı zaten panelin en üstündeki özet kutularında var ve tek
//     başına bir şey söylemiyor (189 kişi olmak iyi de olabilir kötü de).
//   • `turnover.total.rate` — AYNI istatistik hemen üstündeki eğilim grafiğinde, üstelik
//     yönüyle birlikte duruyor. Aynı sayıyı iki kez göstermek yer harcamaktan başka bir işe
//     yaramıyordu.
//   • `retention.rate.12m` — 2026-08-03 kullanıcı kararıyla kaldırıldı: panelde gereksiz yer
//     kaplıyordu.
//   • `headcount.vacancy.rate` — 2026-08-04 kullanıcı kararıyla kaldırıldı: kalan son karttı.
//     Kaldırılan istatistiklerin hepsi Genel Bakış'taki akıllı kart sırasında ve metrik
//     gezgininde hâlâ görülebilir. Bu yüzden metrik isteği artık YALNIZ eğilim serisi için
//     atılır; kart listesi/etiket eşlemesi de koddan çıkarıldı.

// Anomali için tek bir metrik seçiliyor: ürünün her tenant'ta en yoğun aylık serisi olan
// plansız devamsızlık. Panelde metrik seçtirmek amacı aşardı — ayrıntı İçgörüler ekranında.
const ANOMALY_METRIC = 'absence.unplanned.rate';

export default function HaIntelligencePanel({ hasHaModule }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { getMetrics, getNarrative, getAnomalies, getCatalog } = useHrAnalyticsApi();

  const [results, setResults] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [anomalyCount, setAnomalyCount] = useState(null);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!hasHaModule) return undefined; // HA yoksa boşuna istek dahi atma (Karar 5).

    let cancelled = false;
    getMetrics({ codes: TREND_CODE, scope: 'tenant', grain: 'month' }).then((res) => {
      if (!cancelled) setResults(res?.data?.data ?? []);
    });
    // Anlatı ve anomali bileşenleri KAPALI olabilir; o durumda uç 409 döner ve Axios
    // interceptor'ı zaten bildirir. Panel bu iki parçayı sessizce gizler — hata toast'ı YOK
    // (CLAUDE.md) ve kapalı bir bileşen için panelin tamamı çökmez.
    getNarrative()
      .then((res) => { if (!cancelled) setNarrative(res?.data?.data ?? null); })
      .catch(() => { if (!cancelled) setNarrative(null); });
    // Radar katalogdan besleniyor: katman başına "kaç metrik gerçekten açık".
    getCatalog()
      .then((res) => { if (!cancelled) setCatalog(res?.data?.data ?? null); })
      .catch(() => { if (!cancelled) setCatalog(null); });
    getAnomalies({ metricCode: ANOMALY_METRIC, scope: 'tenant' })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data?.data;
        // `evaluated: false` = "değerlendirilemedi", "anomali yok" DEĞİL — ikisini
        // karıştırmamak için sayaç null bırakılır ve satır hiç gösterilmez.
        setAnomalyCount(d?.evaluated ? (d.points || []).filter((p) => p.isAnomaly).length : null);
      })
      .catch(() => { if (!cancelled) setAnomalyCount(null); });

    return () => { cancelled = true; };
  }, [hasHaModule, getMetrics, getNarrative, getAnomalies, getCatalog]);

  // Eğilim serisi: aynı `getMetrics` cevabından okunur, İKİNCİ BİR İSTEK atılmaz.
  const trendCells = useMemo(() => {
    if (!results) return null;
    const r = results.find((x) => x.code === TREND_CODE);
    if (!r || r.state !== 'available') return null;
    return Array.isArray(r.cells) && r.cells.length >= 2 ? r.cells : null;
  }, [results]);

  if (!hasHaModule) return null;

  // Panel, gösterecek TEK bir şeyi bile yoksa hiç render edilmez — boş bir "İK Zekâsı"
  // çerçevesi modülü değerli değil, bozuk gösterirdi.
  if (!narrative && !trendCells && !catalog) return null;

  return (
    <Panel data-role="ha-intelligence-panel">
      <PanelHeader>
        <GradientDot><Sparkles size={16} /></GradientDot>
        <PanelTitle>{t('dashboard.haPanel.title')}</PanelTitle>
        <MoreLink type="button" onClick={() => history.push('/hr-analytics/insights')}>
          {t('dashboard.haPanel.more')}
          <ArrowRight size={15} />
        </MoreLink>
      </PanelHeader>

      <Body>
        <Narrative>
          {narrative ? (
            <NarrativeText>{narrative.text}</NarrativeText>
          ) : (
            <Muted>{t('dashboard.haPanel.noNarrative')}</Muted>
          )}

          {anomalyCount !== null && (
            <AnomalyLine>
              <Activity size={15} />
              {anomalyCount > 0
                ? t('dashboard.haPanel.anomaliesFound', { count: anomalyCount })
                : t('dashboard.haPanel.noAnomalies')}
            </AnomalyLine>
          )}
        </Narrative>

        <ChartsRow>
          <HaCoverageRadar catalog={catalog} />
          {trendCells && (
            <HaTrendChart
              label={t('hrAnalytics.dashboardMetrics.turnoverTotalRate')}
              cells={trendCells}
              locale={i18n.language}
              lowerIsBetter
            />
          )}
        </ChartsRow>
      </Body>
    </Panel>
  );
}
